import * as m from "@paraglide/messages"
import type { NewsItem, SupportedLocale } from "@playbrain/content-types"
import { PlaydataNewsletterForm } from "@playdata/PlaydataNewsletterForm"
import {
  PlaydataButtonAnchor,
  PlaydataButtonLink,
  PlaydataFigure,
  PlaydataHeading,
  PlaydataLead,
  PlaydataSection,
} from "@playdata/PlaydataPrimitives"
import PlaydataReportGallery from "@playdata/PlaydataReportGallery"
import { PlaydataSpeechBubbleList } from "@playdata/PlaydataSpeechBubble"
import { playdataPublishDate } from "@playdata/playdata-date"
import { playdataNewsCoverSrc } from "@playdata/playdata-news-covers"
import { usePlaydataSampleReportUrl } from "@playdata/playdata-settings-context"
import { Link } from "@tanstack/react-router"
import { FileBarChart2, Gamepad2, Headset, Newspaper, TrendingUp } from "lucide-react"

/**
 * The two locales are different pages on the live site, not translations of
 * one page, so each is laid out from its own screenshots.
 *
 * Japanese: dark hero with the chart on the left and centred copy on the
 * right; the bright-blue report promo; services, "what is PLAYDATA", news and
 * the newsletter on alternating white and light bands, each under a green icon.
 *
 * English: a text-only centred hero that offers the sample PDF; the same promo;
 * the six pain points as speech bubbles; "reports make analysis easy" with the
 * arena photograph; the six-page sample gallery; and the streamer tool. No
 * services cards, news strip or newsletter.
 */
export function PlaydataHome({ locale, news }: { locale: SupportedLocale; news: NewsItem[] }) {
  if (locale === "en") return <EnglishHome locale={locale} />
  return (
    <JapaneseHome
      locale={locale}
      news={news}
    />
  )
}

function JapaneseHome({ locale, news }: { locale: SupportedLocale; news: NewsItem[] }) {
  const sampleReportUrl = usePlaydataSampleReportUrl(locale)
  const services = [
    {
      body: m.playdata_home_services_event_body({}, { locale }),
      cta: m.playdata_home_services_event_cta({}, { locale }),
      image: "/images/playdata/event-report-card.jpg",
      key: "event",
      title: m.playdata_home_services_event_title({}, { locale }),
      to: "/$locale/playdata/event-report",
    },
    {
      body: m.playdata_home_services_streamer_body({}, { locale }),
      cta: m.playdata_home_services_streamer_cta({}, { locale }),
      image: "/images/playdata/streamer-report-card.jpg",
      key: "streamer",
      title: m.playdata_home_services_streamer_title({}, { locale }),
      to: "/$locale/playdata/streamer-report",
    },
  ]

  return (
    <>
      <PlaydataSection
        className="py-[5rem] md:py-[7rem]"
        tone="dark"
      >
        <div className="grid items-center gap-[4rem] md:grid-cols-2">
          <PlaydataFigure
            alt={m.playdata_home_hero_imageAlt({}, { locale })}
            height={900}
            src="/images/playdata/mirror-trend.png"
            width={1600}
          />
          <div className="flex flex-col items-center gap-[2.4rem] text-center">
            <h1 className="text-[3rem] leading-[1.4] md:text-[3.9rem]">
              <strong>{m.playdata_home_hero_heading({}, { locale })}</strong>
            </h1>
            <PlaydataLead
              centered
              className="text-[1.5rem] md:text-[1.6rem]"
            >
              {m.playdata_home_hero_lead({}, { locale })}
            </PlaydataLead>
            <PlaydataButtonLink
              className="min-w-[30rem]"
              params={{ locale }}
              to="/$locale/contact"
              variant="hero"
            >
              {m.playdata_common_contactCta({}, { locale })}
            </PlaydataButtonLink>
          </div>
        </div>
      </PlaydataSection>

      <PlaydataSection tone="blue">
        <div className="grid items-center gap-[4rem] md:grid-cols-2">
          <div className="flex flex-col items-start gap-[2rem]">
            <p className="bg-play-white px-[2.4rem] py-[0.8rem] text-[1.4rem] text-play-pink tracking-[0.06em]">
              {m.playdata_home_promo_eyebrow({}, { locale })}
            </p>
            <h2 className="text-[3.2rem] leading-[1.35] md:text-[4.2rem]">
              <strong>{m.playdata_home_promo_heading({}, { locale })}</strong>
            </h2>
            <p className="text-[1.5rem] leading-[1.7] md:text-[1.6rem]">
              {m.playdata_home_promo_body({}, { locale })}
              <br />
              {m.playdata_home_promo_bodyDetail({}, { locale })}
            </p>
            <PlaydataButtonLink
              params={{ locale }}
              to="/$locale/playdata/report"
            >
              {m.playdata_home_promo_cta({}, { locale })}
            </PlaydataButtonLink>
          </div>
          <img
            alt=""
            className="h-auto w-full"
            height={900}
            loading="lazy"
            src="/images/playdata/report-2025-cover.jpg"
            width={1600}
          />
        </div>
      </PlaydataSection>

      <PlaydataSection>
        <PlaydataHeading
          className="mb-[4rem]"
          icon={Headset}
        >
          {m.playdata_home_services_heading({}, { locale })}
        </PlaydataHeading>
        <div className="grid gap-[4rem] md:grid-cols-2">
          {services.map((service) => (
            <article
              className="flex flex-col items-center gap-[2rem] text-center"
              key={service.key}
            >
              <h3 className="text-[2rem]">
                <strong>{service.title}</strong>
              </h3>
              <PlaydataFigure
                alt=""
                height={900}
                src={service.image}
                width={1600}
              />
              <p className="text-left text-[1.5rem] leading-[1.7]">{service.body}</p>
              <PlaydataButtonLink
                params={{ locale }}
                to={service.to}
              >
                {service.cta}
              </PlaydataButtonLink>
            </article>
          ))}
        </div>
      </PlaydataSection>

      <PlaydataSection tone="light">
        <PlaydataHeading
          className="mb-[3.2rem]"
          icon={TrendingUp}
        >
          {m.playdata_home_about_heading({}, { locale })}
        </PlaydataHeading>
        <div className="mx-auto flex max-w-[80ch] flex-col gap-[1.6rem] text-[1.6rem] leading-[1.7] md:text-[1.8rem]">
          <p>{m.playdata_home_about_body1({}, { locale })}</p>
          <p>{m.playdata_home_about_body2({}, { locale })}</p>
        </div>
        <div className="mt-[3.2rem] flex justify-center">
          <PlaydataButtonLink
            params={{ locale }}
            to="/$locale/playdata/about"
          >
            {m.playdata_home_about_cta({}, { locale })}
          </PlaydataButtonLink>
        </div>
      </PlaydataSection>

      {news.length > 0 ? (
        <PlaydataSection>
          <PlaydataHeading
            className="mb-[4rem]"
            icon={Newspaper}
          >
            {m.playdata_home_news_heading({}, { locale })}
          </PlaydataHeading>
          <ul className="grid list-none gap-[2.4rem] p-0 md:grid-cols-3">
            {news.slice(0, 3).map((post) => {
              const slug = typeof post.slug === "string" ? post.slug : (post.slug?.current ?? "")
              const cover = playdataNewsCoverSrc(slug, post.newsCoverImage)
              return (
                <li key={`${post.language}-${slug}`}>
                  <Link
                    className="group flex h-full flex-col gap-[1.2rem]"
                    params={{ locale, slug }}
                    to="/$locale/playdata/news/$slug"
                  >
                    {cover ? (
                      <img
                        alt=""
                        className="h-auto w-full border border-[#dce1e1] shadow-[2px_2px_5px_rgba(0,0,0,0.07)]"
                        height={450}
                        loading="lazy"
                        src={cover}
                        width={800}
                      />
                    ) : null}
                    {post.publishDate ? (
                      <time
                        className="text-[1.3rem]"
                        dateTime={post.publishDate}
                      >
                        {playdataPublishDate(post.publishDate)}
                      </time>
                    ) : null}
                    <h3 className="text-[1.9rem] leading-[1.4] group-hover:underline">
                      <strong>{post.title}</strong>
                    </h3>
                    <span className="mt-auto text-[1.3rem] text-play-pink underline underline-offset-4">
                      {m.playdata_home_news_readMore({}, { locale })} →
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </PlaydataSection>
      ) : null}

      <PlaydataSection>
        <div className="flex flex-col items-center gap-[2rem] text-center">
          <h2 className="text-[2.4rem]">{m.playdata_home_newsletter_heading({}, { locale })}</h2>
          <PlaydataLead centered>{m.playdata_home_newsletter_body({}, { locale })}</PlaydataLead>
          <PlaydataNewsletterForm locale={locale} />
        </div>
      </PlaydataSection>

      <PlaydataSection
        className="py-[4rem]"
        tone="light"
      >
        <div className="flex flex-col items-center gap-[1.6rem] text-center">
          <PlaydataLead centered>{m.playdata_home_sample_body({}, { locale })}</PlaydataLead>
          <PlaydataButtonAnchor
            href={sampleReportUrl}
            rel="noopener"
            target="_blank"
          >
            {m.playdata_home_sample_cta({}, { locale })}
          </PlaydataButtonAnchor>
        </div>
      </PlaydataSection>
    </>
  )
}

function EnglishHome({ locale }: { locale: SupportedLocale }) {
  const sampleReportUrl = usePlaydataSampleReportUrl(locale)
  const pains = [
    m.playdata_home_benefits_item1({}, { locale }),
    m.playdata_home_benefits_item4({}, { locale }),
    m.playdata_home_benefits_item2({}, { locale }),
    m.playdata_home_benefits_item5({}, { locale }),
    m.playdata_home_benefits_item3({}, { locale }),
    m.playdata_home_benefits_item6({}, { locale }),
  ]

  return (
    <>
      <PlaydataSection
        className="py-[6rem] md:py-[8rem]"
        tone="dark"
      >
        <div className="mx-auto flex max-w-[60ch] flex-col items-center gap-[2.4rem] text-center">
          <h1 className="text-[3rem] leading-[1.35] md:text-[3.9rem]">
            <strong>{m.playdata_home_hero_heading({}, { locale })}</strong>
          </h1>
          <PlaydataLead
            centered
            className="text-[1.5rem] md:text-[1.6rem]"
          >
            {m.playdata_home_hero_lead({}, { locale })}
          </PlaydataLead>
          <PlaydataButtonAnchor
            href={sampleReportUrl}
            rel="noopener"
            target="_blank"
            variant="hero"
          >
            {m.playdata_home_hero_cta({}, { locale })}
          </PlaydataButtonAnchor>
          {/*
           * The English hero shows what the report looks like, the same page
           * of it the live site puts here. Japanese leads with the mirror
           * chart instead; the two heroes are different pages, not
           * translations, so the image belongs to this one alone.
           */}
          <PlaydataFigure
            alt={m.playdata_home_hero_imageAlt({}, { locale })}
            className="mt-[1.6rem] max-w-[760px]"
            height={1080}
            src="/images/playdata/sample-en-4.jpg"
            width={1920}
          />
        </div>
      </PlaydataSection>

      <PlaydataSection tone="blue">
        <div className="grid items-center gap-[4rem] md:grid-cols-2">
          <div className="flex flex-col items-start gap-[2rem]">
            <p className="bg-play-white px-[2.4rem] py-[0.8rem] text-[1.4rem] text-play-pink tracking-[0.06em]">
              {m.playdata_home_promo_eyebrow({}, { locale })}
            </p>
            <h2 className="text-[3.2rem] leading-[1.35] md:text-[4.2rem]">
              <strong>{m.playdata_home_promo_heading({}, { locale })}</strong>
            </h2>
            <p className="text-[1.5rem] leading-[1.7] md:text-[1.6rem]">
              {m.playdata_home_promo_body({}, { locale })}
              <br />
              {m.playdata_home_promo_bodyDetail({}, { locale })}
            </p>
            <PlaydataButtonLink
              params={{ locale }}
              to="/$locale/playdata/report"
            >
              {m.playdata_home_promo_cta({}, { locale })}
            </PlaydataButtonLink>
          </div>
          <img
            alt=""
            className="h-auto w-full"
            height={900}
            loading="lazy"
            src="/images/playdata/report-2025-cover.jpg"
            width={1600}
          />
        </div>
      </PlaydataSection>

      <PlaydataSection>
        <PlaydataHeading
          className="mb-[4rem]"
          icon={Gamepad2}
          size="sub"
        >
          <strong>{m.playdata_home_benefits_heading({}, { locale })}</strong>
        </PlaydataHeading>
        <PlaydataSpeechBubbleList items={pains} />
      </PlaydataSection>

      <PlaydataSection tone="light">
        <PlaydataHeading
          className="mb-[4rem]"
          icon={TrendingUp}
          size="sub"
        >
          <strong>{m.playdata_home_reports_heading({}, { locale })}</strong>
        </PlaydataHeading>
        <div className="grid items-center gap-[4rem] md:grid-cols-2">
          <PlaydataFigure
            alt=""
            height={900}
            src="/images/playdata/esports-tournament.jpg"
            width={1600}
          />
          <div className="flex flex-col items-start gap-[1.6rem] text-[1.5rem] leading-[1.7]">
            <p>{m.playdata_home_reports_body1({}, { locale })}</p>
            <p>{m.playdata_home_reports_body2({}, { locale })}</p>
            <PlaydataButtonLink
              className="self-center"
              params={{ locale }}
              to="/$locale/playdata/about"
            >
              {m.playdata_home_reports_cta({}, { locale })}
            </PlaydataButtonLink>
          </div>
        </div>
      </PlaydataSection>

      <PlaydataSection>
        <PlaydataHeading
          className="mb-[4rem]"
          icon={FileBarChart2}
          size="sub"
        >
          <strong>{m.playdata_home_sample_heading({}, { locale })}</strong>
        </PlaydataHeading>
        <PlaydataReportGallery locale={locale} />
        <div className="mt-[4rem] flex flex-col items-center gap-[2.4rem] text-center">
          <p className="text-[1.5rem]">{m.playdata_home_sample_more({}, { locale })}</p>
          <PlaydataButtonAnchor
            href={sampleReportUrl}
            rel="noopener"
            target="_blank"
          >
            {m.playdata_home_sample_cta({}, { locale })}
          </PlaydataButtonAnchor>
        </div>
      </PlaydataSection>

      <PlaydataSection tone="light">
        <PlaydataHeading
          className="mb-[4rem]"
          icon={Headset}
          size="sub"
        >
          <strong>{m.playdata_home_streamers_heading({}, { locale })}</strong>
        </PlaydataHeading>
        <div className="grid items-center gap-[4rem] md:grid-cols-2">
          <div className="flex flex-col items-start gap-[2rem]">
            <p className="text-[1.5rem] leading-[1.7]">{m.playdata_home_streamers_body({}, { locale })}</p>
            <PlaydataButtonLink
              className="self-center"
              params={{ locale }}
              to="/$locale/contact"
            >
              {m.playdata_common_contactCta({}, { locale })}
            </PlaydataButtonLink>
          </div>
          <PlaydataFigure
            alt=""
            height={900}
            src="/images/playdata/streamer-tool.png"
            width={1600}
          />
        </div>
      </PlaydataSection>
    </>
  )
}

export default PlaydataHome
